import DOMPurify from "dompurify";
import { marked } from "marked";

/** Markdown to sanitised HTML.
 *
 * Sanitised even though instructions are usually ours: a candidate writes
 * Markdown too, and this pane will later render text an agent produced.
 */
export function renderMarkdown(source: string): string {
  const raw = marked.parse(source, { async: false }) as string;
  return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
}


/** Close what has not finished arriving, so partial markdown still renders.
 *
 * Streaming text is markdown caught mid-sentence: a fence may be open, a bold
 * run unterminated. Parsing that raw makes an unclosed ``` swallow the rest of
 * the answer, which is why a naive implementation renders plain text while
 * streaming and then snaps into formatting at the end. Balancing the open
 * constructs instead means the same renderer runs throughout and nothing jumps.
 *
 * Only the tail is touched, and only enough to parse — the text itself is never
 * altered.
 */
export function balanceMarkdown(text: string): string {
  let out = text;

  // An odd number of fences means one is still open.
  const fences = out.match(/^```/gm)?.length ?? 0;
  if (fences % 2 === 1) out += "\n```";

  // Inline code, counted outside fenced blocks.
  const withoutFences = out.replace(/```[\s\S]*?```/g, "");
  if ((withoutFences.match(/`/g)?.length ?? 0) % 2 === 1) out += "`";

  // Emphasis, which otherwise renders its own asterisks as literal text.
  const bold = withoutFences.match(/\*\*/g)?.length ?? 0;
  if (bold % 2 === 1) out += "**";

  return out;
}

/** Markdown that may still be arriving. */
export function renderStreamingMarkdown(source: string): string {
  return renderMarkdown(balanceMarkdown(source));
}
